import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {FlatList, Pressable, Text, View} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import BaseAuthScreen from 'src/modules/authentication/screens/base-auth-screen';
import {Typography} from 'src/styles';
import * as Colors from 'src/styles/colors';
import {login} from 'src/redux/auth';
import {TouchableOpacity} from 'react-native-gesture-handler';
import {logout} from 'src/redux/auth';
import {useAnalytics} from 'src/providers/segment';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import {compose} from 'recompose';
import {useNavigation} from '@react-navigation/native';
import sync from 'src/providers/watermelondb/sync';
import {useDatabase} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import AsyncStorage from '@react-native-async-storage/async-storage';
import FileImage from 'src/design-system/file-image';
import {useStyle} from 'src/providers/style';
import {useTranslations} from 'src/providers/translation';
import RHButton from '../../../../common-components/custom-ui-helpers/RHButton';
import {useSyncControl} from 'src/contexts/SyncContext';
import Spinner from 'react-native-loading-spinner-overlay';
import syncWithRetry from 'src/providers/watermelondb/sync/sync-with-retry';

const OrganizationSelectionScreen = ({instances}: any) => {
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const navigation = useNavigation();
  const database = useDatabase();
  const styles = useStyle();
  const translations = useTranslations();
  const {setIntervalSyncDisabled} = useSyncControl();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const fetchUser = async () => {
    const users = await database
      .get('users')
      .query(Q.and(Q.where('identifier', await AsyncStorage.getItem('id'))))
      .fetch();

    const user = users?.[0];

    setData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    });
  };

  const firstName = data?.firstName || '';
  const lastName = data?.lastName || '';
  const email = data?.email || '';

  const selectOrganization = async (partition: any) => {
    const users = await database
      .get('users')
      .query(
        Q.and(
          Q.where('identifier', await AsyncStorage.getItem('id')),
          Q.where('_partition', partition),
        ),
      )
      .fetch();

    if (users.length) {
      setError('');
      await dispatch(
        login({
          userId: users[0].id,
          selectedGroup: partition,
        }),
      );
      analytics.track('User Signed In', {
        username: data?.email,
        context: {
          groupId: partition,
        },
        groupId: [partition],
      });
      setIsLoading(true);

      try {
        // Check if sync is in progress
        const _orgSyncs = await AsyncStorage.getItem('organizationSyncs');
        const organizationSyncs = _orgSyncs ? JSON.parse(_orgSyncs) : {};

        if (organizationSyncs[partition]) {
          const timestamp = organizationSyncs[partition].lastPulledAt;

          await syncWithRetry(
            {
              database,
              table: true,
              lastPulledTimestamp: timestamp,
              partition,
            },
            {
              maxRetries: 5,
              retryDelay: 2000,
              waitForExisting: true, // Wait for existing sync
              maxWaitTime: 30000, // Wait up to 30 seconds
            },
          );

          navigation.navigate('Dashboard');
        } else {
          // First time syncing this organization
          await syncWithRetry(
            {
              database,
              table: true,
              lastPulledTimestamp: 0,
              partition,
            },
            {
              maxRetries: 5,
              retryDelay: 2000,
              waitForExisting: true,
              maxWaitTime: 30000,
            },
          );

          organizationSyncs[partition] = {
            lastPulledAt: 0,
          };
          await AsyncStorage.setItem(
            'organizationSyncs',
            JSON.stringify(organizationSyncs),
          );
          navigation.navigate('Dashboard');
        }

        setIsLoading(false);

        // Dashboard sync
        syncWithRetry(
          {database, onDashboard: true, partition},
          {
            maxRetries: 1,
            waitForExisting: false,
          },
        ).catch(err => {
          console.log('Dashboard sync failed:', err);
        });
      } catch (e) {
        console.log('Organization sync error:', e);
        setIsLoading(false);
      }
    } else {
      setError(translations('auth_screen_sync_error'));
      setIsLoading(true);

      try {
        await syncWithRetry(
          {database, onLogin: true},
          {
            maxRetries: 3,
            retryDelay: 2000,
            waitForExisting: true,
            maxWaitTime: 20000,
          },
        );
        fetchUser();
      } catch (e) {
        console.log('Login sync error:', e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (database) {
      setIsLoading(true);
      sync({database}).then(() => {
        fetchUser();
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database]);

  useEffect(() => {
    setIntervalSyncDisabled(true);

    return () => {
      setIntervalSyncDisabled(false);
    };
  }, [setIntervalSyncDisabled]);

  const submitLogout = async () => {
    await database.write(async () => {
      if (!database._isBeingReset) {
        await database.unsafeResetDatabase();
      }
    });
    dispatch(logout());
    navigation.navigate('Login');
  };

  const tryAgain = async () => {
    setIsLoading(true);
    await sync({database, table: true});
    setIsLoading(false);
  };

  return (
    <BaseAuthScreen>
      <Text
        style={[
          Typography.H1,
          styles.textAlignCenter,
          styles.textColorPrimary,
          styles.marginLTop,
        ]}>
        {translations('auth_screen_organizations')}
      </Text>
      <Text
        style={[Typography.P3, styles.textAlignCenter, styles.marginLVertical]}>
        {translations('auth_screen_select_organization_to_access')}
      </Text>
      <View style={[styles.column, styles.alignCenter]}>
        <View
          style={[
            styles.padding,
            styles.backgroundColorTeal300,
            styles.borderRadius,
            styles.menuMaxWidth,
            styles.width,
          ]}>
          <Text style={[Typography.P3]}>
            {firstName} {lastName}
          </Text>
          <Text style={[Typography.P3]}>{email}</Text>
        </View>
        <View
          style={[
            styles.marginVertical,
            styles.padding,
            styles.backgroundColorWhite,
            styles.borderRadius,
            styles.menuMaxWidth,
            styles.width,
            styles.elevation,
          ]}>
          <Text style={[Typography.H4]}>
            {translations('auth_screen_select_organization')}
          </Text>
          {isLoading ? (
            <>
              <Text style={[Typography.P3, styles.textColorPrimary]}>
                {error}
              </Text>
              <Spinner
                visible={isLoading}
                textContent={
                  'Please wait while the page loads completely to ensure proper data syncing. Thank you for your patience!'
                }
                overlayColor={Colors.OPACITY_54}
                textStyle={[
                  styles.textColorWhite,
                  Typography.P2_MEDIUM,
                  styles.paddingHorizontal,
                  styles.height200,
                  styles.maxWindowWidth90,
                  /* eslint-disable-next-line react-native/no-inline-styles */
                  {marginTop: 90},
                ]}
              />
            </>
          ) : (
            <>
              {instances.length === 0 ? (
                <>
                  <Text style={[Typography.P3, styles.textColorPrimary]}>
                    The sync may have failed. Press below to try another
                    attempt.
                  </Text>
                  <View style={[styles.column]}>
                    <RHButton
                      mode="contained"
                      onPress={() => tryAgain()}
                      style={[styles.marginLTop]}>
                      Try Again
                    </RHButton>
                  </View>
                </>
              ) : (
                <>
                  <Text style={[Typography.P3, styles.textColorPrimary]}>
                    These are organizations you have access to:
                  </Text>
                  <FlatList
                    scrollEnabled={false}
                    data={instances}
                    renderItem={({item}) => {
                      return (
                        <Pressable
                          key={item.id}
                          style={({pressed}) => [
                            styles.row,
                            styles.justifySpaceBetween,
                            styles.paddingL,
                            styles.alignItemsCenter,
                            styles.border,
                            styles.borderColorPrimary300,
                            styles.borderRadiusSM,
                            styles.marginSMVertical,
                            {
                              opacity: pressed ? 0.4 : 1,
                            },
                          ]}
                          onPress={() => selectOrganization(item.partition)}>
                          <View
                            style={[
                              styles.flex,
                              styles.row,
                              styles.alignCenter,
                            ]}>
                            {item?.logo ? (
                              <View
                                style={[
                                  styles.width40,
                                  styles.height40,
                                  styles.alignCenter,
                                  styles.justifyCenter,
                                  styles.marginMRight,
                                  styles.overflowHidden,
                                  styles.borderRadius,
                                ]}>
                                <FileImage src={item?.logo} />
                              </View>
                            ) : (
                              <></>
                            )}
                            <Text
                              style={[
                                styles.flex,
                                Typography.P2,
                                styles.textColorPrimary,
                                styles.flexWrap,
                              ]}>
                              {item.name}
                            </Text>
                          </View>
                          <View
                            style={[
                              styles.column,
                              styles.alignCenter,
                              styles.justifyCenter,
                            ]}>
                            <Icon
                              name="chevron-right"
                              color={Colors.OPACITY_54}
                              size={20}
                            />
                          </View>
                        </Pressable>
                      );
                    }}
                  />
                </>
              )}
            </>
          )}
        </View>
      </View>
      <View
        style={[
          styles.marginHorizontal,
          styles.justifyCenter,
          styles.alignCenter,
        ]}>
        <Text style={[Typography.P3, styles.textColorPrimary]}>
          {translations('auth_screen_not_seeing_organization')}
        </Text>
        <TouchableOpacity onPress={submitLogout}>
          <Text style={[Typography.P3, styles.textColorPrimary]}>
            {translations('auth_screen_different_email')}
          </Text>
        </TouchableOpacity>
      </View>
      {/*<View style={styles.organizationDiv}>*/}
      {/*  <Text style={Typography.H4}>Create a new organization</Text>*/}
      {/*  <Text style={[Typography.P3, styles.textColorPrimary]}>*/}
      {/*    These are organizations you have access to*/}
      {/*  </Text>*/}
      {/*  <TextInput*/}
      {/*    mode="outlined"*/}
      {/*    placeholder="Name your organization"*/}
      {/*    style={styles.nameColor}*/}
      {/*    theme={{colors: {primary: Colors.BLUE_RIBBON}}}*/}
      {/*  />*/}
      {/*  <HelperText type="info" visible={true} style={styles.marginBottom}>*/}
      {/*    New organization name*/}
      {/*  </HelperText>*/}
      {/*  <RHButton*/}
      {/*    mode={'contained'}*/}
      {/*    onPress={createOrganization}*/}
      {/*    secondary*/}
      {/*    style={styles.button}>*/}
      {/*    CREATE A NEW ORGANIZATION*/}
      {/*  </RHButton>*/}
      {/*</View>*/}
    </BaseAuthScreen>
  );
};

export default compose(
  withDatabase,
  withObservables([], ({database}) => ({
    instances: database.get('instances').query(),
  })),
)(OrganizationSelectionScreen);
